import { Compass, Target, HeartHandshake, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const values = [
  { icon: HeartHandshake, title: "Partnership, not paperwork", text: "We walk beside you from your first idea to your growing business — a partner for the whole journey, not a one-time transaction." },
  { icon: ShieldCheck, title: "Trust above all", text: "Your future is precious. Every step we take is transparent, compliant and designed to protect what matters to you." },
  { icon: Sparkles, title: "Premium, human service", text: "Global standards with a personal touch. Real people who understand the Indian journey and the Portuguese system." },
  { icon: TrendingUp, title: "Built for growth", text: "Portugal is your launchpad. We think beyond arrival — towards the business and life you'll build for years to come." },
];

export default function AboutPage() {
  return (
    <Layout>
      <Seo title="About Effizent Europe — Your Portugal Partner" description="Effizent Europe is not an immigration consultancy. We are a Portugal Business, Relocation and Settlement Partner helping Indians build their future in Portugal." />
      <PageHero eyebrow="About" title="We're not an immigration agency." subtitle="Effizent Europe is your Portugal Business, Relocation & Settlement Partner. A visa is one milestone. Building your future is the mission." crumbs={[{ label: "About" }]} />

      <div className="max-w-4xl mx-auto px-6 md:px-8 py-20">
        <Reveal>
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold mb-8"><Compass className="h-7 w-7" strokeWidth={1.4} /></span>
          <h2 className="font-playfair text-3xl md:text-4xl text-navy leading-snug">Born from a journey we lived ourselves.</h2>
          <div className="mt-6 space-y-5 text-navy/70 text-lg leading-relaxed">
            <p>Effizent Europe began with a simple truth: moving to Portugal is not the hard part — building a real future there is. Too many talented Indians arrive with a visa but no plan, no company, no ground support and no one who truly understands both worlds.</p>
            <p>We exist to change that. From our home in Porto, we help entrepreneurs, students, professionals and families navigate every milestone — business consulting, company formation, NIF, banking, visa, relocation, accounting, hiring and expansion across Europe.</p>
            <p className="font-playfair text-2xl text-navy italic">"We don't just help you move to Portugal. We help you build your future in Portugal."</p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-16 rounded-3xl bg-navy text-warm p-10 relative overflow-hidden">
            <div className="spotlight absolute inset-0 pointer-events-none" />
            <p className="accent-eyebrow text-gold flex items-center gap-2"><Target className="h-4 w-4" /> Our positioning</p>
            <p className="font-playfair text-2xl md:text-3xl mt-5 leading-snug relative">The digital operating system for Indians building a future in Portugal — from your first idea to expansion across the European Union.</p>
          </div>
        </Reveal>

        <div className="mt-16 grid sm:grid-cols-2 gap-5">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.06}>
              <div className="rounded-3xl bg-white border border-navy/10 p-8 h-full">
                <v.icon className="h-8 w-8 text-gold" strokeWidth={1.4} />
                <h3 className="font-playfair text-xl text-navy mt-5">{v.title}</h3>
                <p className="text-navy/60 mt-3 leading-relaxed">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 grid sm:grid-cols-2 gap-5">
            {CONTACT.offices.map((o) => (
              <div key={o.country} className="rounded-3xl border border-navy/10 bg-white p-7 text-center">
                <p className="accent-eyebrow text-terracotta">{o.country} Office</p>
                <p className="font-playfair text-lg text-navy mt-3">{o.address}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-navy/70 text-lg">
            <a href="/about.html" data-testid="about-full-link" className="text-maroon-accent font-600 link-underline">Read more about our team and offices</a>
          </p>
        </Reveal>
      </div>
    </Layout>
  );
}
